import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { debounce } from "lodash/function";
import { groupBy } from "lodash";
import * as notify from "shared/notify";

const SEARCH_DELAY = 300;

const useValidation = () => {
    const checkValidation = ({ minLength, greaterThanStartDate }, value) => {
        if (minLength) {
            return value.length < minLength;
        }
    };

    const validateDateRange = (startDate, endDate) => {
        return new Date(endDate) < new Date(startDate);
    };

    const validationFields = useMemo(() => {
        return [
            {
                label: "Brand",
                name: "brand",
                minLength: 2,
            },
            {
                label: "Manufacturer",
                name: "manufacturer",
                minLength: 2,
            },
            {
                label: "UPC",
                name: "upc",
                minLength: 3,
            },
            {
                label: "UPC",
                name: "context.upc",
                minLength: 3,
            },
            {
                label: "Tracking Number",
                name: "binTrackingNumber",
                minLength: 3,
            },
            {
                label: "Tracking Number",
                name: "context.binTrackingNumber",
                minLength: 3,
            },
            {
                label: "Tracking Numbers",
                name: "trackingNumbers",
                minLength: 6,
            },
            {
                label: "Tracking Numbers",
                name: "shipment.trackingNumbers",
                minLength: 6,
            },
            {
                label: "Tracking Numbers",
                name: "context.trackingNumbers",
                minLength: 6,
            },
            {
                label: "Client",
                name: "client",
                minLength: 3,
            },
            {
                label: "Description",
                name: "description",
                minLength: 3,
            },
        ];
    }, []);

    return useCallback((filters) => {
        const invalidFields = validationFields.filter((field) => {
            return filters.some(({ id, value }) => {
                // if value is an array, we assume it's a multiselect field and it's always valid
                if (Array.isArray(value)) {
                    return false;
                }

                return id === field.name && checkValidation(field, value);
            });
        });

        const startDate = filters.find(({ id }) => id === "startDate");
        const endDate = filters.find(({ id }) => id === "endDate");

        // find a better way for validation strategy
        if (startDate && endDate) {
            const isEndDateGreaterThanStartDate = validateDateRange(startDate.value, endDate.value);

            if (!isEndDateGreaterThanStartDate) {
                return invalidFields;
            }

            invalidFields.push({ id: "endDate", greaterThan: true });
        }

        return invalidFields;
    }, []);
};

const useTranslateParams = (params) => {
    // don't take totalPages and totalCount into account
    const translateParams = ({ totalPages, totalCount, ...restParams }) => {
        let paginationParams = { ...restParams };
        const [sortParams] = restParams.sortBy;

        if (sortParams) {
            paginationParams.originalSort = restParams.sortBy;
            paginationParams.sortBy = sortParams.id;
            paginationParams.order = sortParams.desc ? "desc" : "asc";
        }

        return Object.keys(paginationParams).map((pagingKey) => {
            return {
                id: pagingKey,
                value: paginationParams[pagingKey],
            };
        });
    };

    const [translatedParams, setTranslatedParams] = useState(translateParams(params));

    const handleParamTranslation = useCallback((newParams) => setTranslatedParams(translateParams(newParams)), []);

    return [translatedParams, handleParamTranslation];
};

const initialState = {
    totalPages: 0,
    totalCount: 0,
    pageNumber: 0,
    pageSize: 10,
    order: "asc",
    sortBy: [],
};

// default values object shape is the same as initialState
export const usePagination = ({ defaultValues = initialState, defaultFilters, filterOutputMappings, onSearchParamsSet, callback }) => {
    const firstUpdate = useRef(true);
    // this state is used ONLY as a ref for "filtered" props
    const [filteredValues, setFilteredValues] = useState(defaultFilters || []);
    const [searchParams, setSearchParams] = useState(defaultFilters || []);
    const [pagingParams, setPagingParams] = useState({ ...initialState, ...defaultValues });
    const [translatedParams, setTranslatedParams] = useTranslateParams(pagingParams, filterOutputMappings);
    const validate = useValidation();

    useEffect(() => {
        if (onSearchParamsSet && filteredValues.length > 0) {
            onSearchParamsSet(filteredValues);
        }
    }, [filteredValues]);

    // fired on every param change
    useEffect(() => {
        // prevent calling cb on init
        if (!firstUpdate.current) {
            setTranslatedParams(pagingParams);
        }

        if (firstUpdate.current) {
            firstUpdate.current = false;
        }
    }, [pagingParams.pageNumber, pagingParams.sortBy, pagingParams.order, pagingParams.pageSize]);

    const hasIdReplacer = (filters) => {
        if (filterOutputMappings) {
            return Object.keys(filterOutputMappings).some((mapperKey) => {
                return filters.some(({ id }) => id === mapperKey);
            });
        }

        return false;
    };

    const replaceMapperIds = (filters) => {
        return filters.map((filter) => {
            const filterIdReplacer = filterOutputMappings[filter.id];

            if (filterIdReplacer) {
                return {
                    id: filterIdReplacer,
                    value: filter.value,
                };
            }

            return filter;
        });
    };

    const handleDelayedSearch = useCallback((filters) => {
        if (filters) {
            const invalidFields = validate(filters);

            if (invalidFields.length) {
                invalidFields.forEach(({ label, minLength, greaterThan }) => {
                    if (minLength) {
                        notify.warning(`Character count for "${label}" must be not less than ${minLength}`);
                    }

                    if (greaterThan) {
                        notify.warning('"To" date must be lesser than "From" date');
                    }
                });

                return;
            }
        }

        // set 1st page on every search attempt
        setPage(defaultValues.pageNumber || initialState.pageNumber);

        // check if filter has replacer output
        if (hasIdReplacer(filters)) {
            setSearchParams(replaceMapperIds(filters));
        } else {
            setSearchParams(filters);
        }

        if (!filters.length && callback) {
            return callback();
        }
    }, []);

    const handleSearch = useCallback(
        debounce((filters = []) => {
            if (!filters) {
                filters = [];
            }

            setFilteredValues(filters);
            handleDelayedSearch(filters);
        }, SEARCH_DELAY),
        []
    );

    const reset = useCallback(() => {
        setFilteredValues([]);
        setSearchParams([]);
        setPagingParams({ ...initialState, ...defaultValues });
    }, []);

    const resetFilters = useCallback(() => {
        // cancel search if "reset" button was pressed (prevent sending 2 requests)
        handleSearch.cancel();
        reset();

        if (callback) {
            callback();
        }
    }, [handleSearch, reset, callback]);

    const handleGroupBy = useCallback((items, groupKey) => groupBy(items, groupKey), []);

    const setOrder = useCallback((order) => setPagingParams((state) => ({ ...state, order })), []);

    const setPage = useCallback((pageNumber) => setPagingParams((state) => ({ ...state, pageNumber })), []);

    const setSortBy = useCallback(
        (sortBy) =>
            setPagingParams((state) => {
                return { ...state, sortBy };
            }),
        []
    );

    const setPageSize = useCallback(
        (pageSize) => setPagingParams((state) => ({ ...state, pageNumber: defaultValues.pageNumber || initialState.pageNumber, pageSize })),
        []
    );

    const setTotalCount = useCallback((totalCount) => setPagingParams((state) => ({ ...state, totalCount })), []);

    // this is an external search function because "upcs" field is not a part of react table component
    const searchByUpcs = useCallback(
        (upcs, mapper) => {
            // exclude "upc" from search to avoid duplicating this param and update filters when value is empty
            const params = filteredValues.filter(({ id }) => id !== (mapper || "upc"));
            const upcValue = upcs ? [{ id: mapper || "upc", value: upcs.split(/\r?\n/).join(",") }] : [];

            handleSearch([...params, ...upcValue]);
        },
        [filteredValues, handleSearch]
    );

    return [
        {
            ...pagingParams,
            totalPages: Math.ceil(pagingParams.totalCount / pagingParams.pageSize),
            pagingParams: translatedParams,
            searchParams,
            filteredValues,
        },
        {
            setPage,
            setOrder,
            setPageSize,
            setSortBy,
            setTotalCount,
            reset,
            search: handleSearch,
            searchByUpcs,
            resetFilters,
            groupBy: handleGroupBy,
        },
    ];
};

export const getPagingParams = (params) => {
    const [pageNumber, pageSize, order, sortBy] = params;

    return {
        pageNumber: parseInt(pageNumber.value, 10),
        pageSize: parseInt(pageSize.value, 10),
        order: order.value,
        sortBy: [
            {
                id: sortBy.value,
                desc: order.value === "desc",
            },
        ],
    };
};
